import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Slider,
  Stack,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  validateArrivalStagingDwell,
  validateDepartureStagingDwell,
  validateQuickTurnThreshold,
} from "../hooks/useParkingConfig";
import { SortingStrategy } from "../sortingStrategies";
import {
  MAX_ARRIVAL_STAGING_MINUTES,
  MAX_DEPARTURE_STAGING_MINUTES,
  MAX_QUICK_TURN_THRESHOLD_MINUTES,
  MIN_QUICK_TURN_THRESHOLD_MINUTES,
} from "../types";
import { ParkingConfigControl } from "./ParkingConfigControl";

/**
 * Formats minutes into a human-readable time string
 * @param minutes - Number of minutes to format
 * @param t - Translation function
 * @returns Formatted string (e.g., "30 min", "1 hour", "1 hour 15 min")
 */
function formatTimeMinutes(minutes: number, t: (key: string) => string): string {
  if (minutes === 0) return `0 ${t('screens.ramp.autoPark.parkingConfigForm.time.min')}`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins} ${t('screens.ramp.autoPark.parkingConfigForm.time.min')}`;
  } else if (mins === 0) {
    return hours === 1 ? `1 ${t('screens.ramp.autoPark.parkingConfigForm.time.hour')}` : `${hours} ${t('screens.ramp.autoPark.parkingConfigForm.time.hours')}`;
  } else {
    return `${hours} ${t('screens.ramp.autoPark.parkingConfigForm.time.hour')}${hours > 1 ? 's' : ''} ${mins} ${t('screens.ramp.autoPark.parkingConfigForm.time.min')}`;
  }
}

export interface ParkingConfigFormProps {
  bufferMinutes: number;
  plannedMoveCap: number;
  allowTrapping: boolean;
  egressWeight: number;
  wasteWeight: number;
  structuralWeight: number;
  zonePriorityWeight: number;
  min_dwell_weight: number;
  sortingStrategy: SortingStrategy;
  arrivalStagingDwellMinutes: number;
  departureStagingDwellMinutes: number;
  quickTurnThresholdMinutes: number;
  onBufferMinutesChange: (value: number) => void;
  onPlannedMoveCapChange: (value: number) => void;
  onAllowTrappingChange: (value: boolean) => void;
  onEgressWeightChange: (value: number) => void;
  onWasteWeightChange: (value: number) => void;
  onStructuralWeightChange: (value: number) => void;
  onZonePriorityWeightChange: (value: number) => void;
  onMinDwellWeightChange: (value: number) => void;
  onSortingStrategyChange: (value: SortingStrategy) => void;
  onArrivalStagingDwellMinutesChange: (value: number) => void;
  onDepartureStagingDwellMinutesChange: (value: number) => void;
  onQuickTurnThresholdMinutesChange: (value: number) => void;
  size?: "small" | "medium";
  layout?: "row" | "column";
  disabledFields?: string[];
  disabledSortingOptions?: SortingStrategy[];
}

/**
 * Reusable parking configuration form
 * Used by both AutoPark dialog and TemporalTest dev page
 */
export const ParkingConfigForm: React.FC<ParkingConfigFormProps> = ({
  bufferMinutes,
  plannedMoveCap,
  allowTrapping,
  egressWeight,
  wasteWeight,
  structuralWeight,
  zonePriorityWeight,
  min_dwell_weight,
  sortingStrategy,
  arrivalStagingDwellMinutes,
  departureStagingDwellMinutes,
  quickTurnThresholdMinutes,
  onBufferMinutesChange,
  onPlannedMoveCapChange,
  onAllowTrappingChange,
  onEgressWeightChange,
  onWasteWeightChange,
  onStructuralWeightChange,
  onZonePriorityWeightChange,
  onMinDwellWeightChange,
  onSortingStrategyChange,
  onArrivalStagingDwellMinutesChange,
  onDepartureStagingDwellMinutesChange,
  onQuickTurnThresholdMinutesChange,
  size = "small",
  layout = "row",
  disabledFields = [],
  disabledSortingOptions = [],
}) => {
  const { t } = useTranslation();

  // Validate staging configuration values
  const arrivalValidation = validateArrivalStagingDwell(
    arrivalStagingDwellMinutes
  );
  const departureValidation = validateDepartureStagingDwell(
    departureStagingDwellMinutes
  );
  const thresholdValidation = validateQuickTurnThreshold(
    quickTurnThresholdMinutes
  );

  return (
    <Stack
      direction={layout}
      spacing={layout === "column" ? 1.5 : 1}
      alignItems={layout === "row" ? "flex-start" : "stretch"}
      flexWrap="wrap"
      sx={layout === "row" ? { p: 1, borderRadius: 1 } : undefined}
    >
      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.buffer.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.buffer.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={bufferMinutes}
            onChange={(e) => onBufferMinutesChange(Number(e.target.value))}
            disabled={disabledFields.includes("bufferMinutes")}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.buffer.options.none')}</MenuItem>
            <MenuItem value={10}>{t('screens.ramp.autoPark.parkingConfigForm.buffer.options.minutes10')}</MenuItem>
            <MenuItem value={15}>{t('screens.ramp.autoPark.parkingConfigForm.buffer.options.standard')}</MenuItem>
            <MenuItem value={20}>{t('screens.ramp.autoPark.parkingConfigForm.buffer.options.high')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={plannedMoveCap}
            onChange={(e) => onPlannedMoveCapChange(Number(e.target.value))}
            disabled={disabledFields.includes("plannedMoveCap")}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.options.none')}</MenuItem>
            <MenuItem value={1}>{t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.options.allow1')}</MenuItem>
            <MenuItem value={2}>{t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.options.allow2')}</MenuItem>
            <MenuItem value={3}>{t('screens.ramp.autoPark.parkingConfigForm.plannedMoves.options.allow3')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.allowTrapping.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.allowTrapping.helpText')}
        layout={layout}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allowTrapping}
              onChange={(e) => onAllowTrappingChange(e.target.checked)}
              size={size}
            />
          }
          label={t('screens.ramp.autoPark.parkingConfigForm.allowTrapping.label')}
        />
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.egressWeight.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.egressWeight.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={egressWeight}
            onChange={(e) => onEgressWeightChange(Number(e.target.value))}
          >
            <MenuItem value={1.0}>{t('screens.ramp.autoPark.parkingConfigForm.egressWeight.options.low')}</MenuItem>
            <MenuItem value={2.0}>{t('screens.ramp.autoPark.parkingConfigForm.egressWeight.options.medium')}</MenuItem>
            <MenuItem value={5.0}>{t('screens.ramp.autoPark.parkingConfigForm.egressWeight.options.high')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={wasteWeight}
            onChange={(e) => onWasteWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.options.disabled')}</MenuItem>
            <MenuItem value={0.5}>{t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.options.low')}</MenuItem>
            <MenuItem value={1.0}>{t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.options.medium')}</MenuItem>
            <MenuItem value={2.0}>{t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.options.high')}</MenuItem>
            <MenuItem value={3.0}>{t('screens.ramp.autoPark.parkingConfigForm.wasteWeight.options.veryHigh')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={structuralWeight}
            onChange={(e) => onStructuralWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.options.disabled')}</MenuItem>
            <MenuItem value={0.05}>{t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.options.low')}</MenuItem>
            <MenuItem value={0.1}>{t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.options.standard')}</MenuItem>
            <MenuItem value={0.2}>{t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.options.medium')}</MenuItem>
            <MenuItem value={0.5}>{t('screens.ramp.autoPark.parkingConfigForm.structuralWeight.options.high')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={zonePriorityWeight}
            onChange={(e) => onZonePriorityWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.options.disabled')}</MenuItem>
            <MenuItem value={0.5}>{t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.options.low')}</MenuItem>
            <MenuItem value={1.0}>{t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.options.standard')}</MenuItem>
            <MenuItem value={2.0}>{t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.options.high')}</MenuItem>
            <MenuItem value={3.0}>{t('screens.ramp.autoPark.parkingConfigForm.zonePriorityWeight.options.veryHigh')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={min_dwell_weight}
            onChange={(e) => onMinDwellWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>{t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.options.disabled')}</MenuItem>
            <MenuItem value={0.5}>{t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.options.low')}</MenuItem>
            <MenuItem value={1.0}>{t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.options.standard')}</MenuItem>
            <MenuItem value={2.0}>{t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.options.high')}</MenuItem>
            <MenuItem value={3.0}>{t('screens.ramp.autoPark.parkingConfigForm.minDwellWeight.options.veryHigh')}</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.arrivalStagingTime.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.arrivalStagingTime.helpText', {
          max: MAX_ARRIVAL_STAGING_MINUTES
        })}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Slider
            value={arrivalStagingDwellMinutes}
            onChange={(_, value) =>
              onArrivalStagingDwellMinutesChange(value as number)
            }
            disabled={disabledFields.includes("arrivalStagingDwellMinutes")}
            min={0}
            max={90}
            step={15}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTimeMinutes(value, t)}
          />
          <FormHelperText sx={{ mt: 1 }}>
            {arrivalStagingDwellMinutes === 0
              ? t('screens.ramp.autoPark.parkingConfigForm.time.disabled')
              : formatTimeMinutes(arrivalStagingDwellMinutes, t)}
            {!arrivalValidation.isValid && (
              <span style={{ marginLeft: "8px" }}>
                {arrivalValidation.message}
              </span>
            )}
          </FormHelperText>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.departureStagingTime.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.departureStagingTime.helpText', {
          max: MAX_DEPARTURE_STAGING_MINUTES
        })}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Slider
            value={departureStagingDwellMinutes}
            onChange={(_, value) =>
              onDepartureStagingDwellMinutesChange(value as number)
            }
            disabled={disabledFields.includes("departureStagingDwellMinutes")}
            min={0}
            max={90}
            step={15}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTimeMinutes(value, t)}
          />
          <FormHelperText sx={{ mt: 1 }}>
            {departureStagingDwellMinutes === 0
              ? t('screens.ramp.autoPark.parkingConfigForm.time.disabled')
              : formatTimeMinutes(departureStagingDwellMinutes, t)}
            {!departureValidation.isValid && (
              <span style={{ marginLeft: "8px" }}>
                {departureValidation.message}
              </span>
            )}
          </FormHelperText>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.helpText', {
          min: MIN_QUICK_TURN_THRESHOLD_MINUTES,
          max: MAX_QUICK_TURN_THRESHOLD_MINUTES
        })}
        layout={layout}
      >
        <FormControl fullWidth size={size} error={!thresholdValidation.isValid}>
          <Select
            value={quickTurnThresholdMinutes}
            onChange={(e) =>
              onQuickTurnThresholdMinutesChange(Number(e.target.value))
            }
            disabled={
              (arrivalStagingDwellMinutes === 0 &&
                departureStagingDwellMinutes === 0) ||
              disabledFields.includes("quickTurnThresholdMinutes")
            }
          >
            <MenuItem value={60}>{t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.options.hour1')}</MenuItem>
            <MenuItem value={90}>{t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.options.hours1_5')}</MenuItem>
            <MenuItem value={120}>{t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.options.standard')}</MenuItem>
            <MenuItem value={180}>{t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.options.hours3')}</MenuItem>
            <MenuItem value={240}>{t('screens.ramp.autoPark.parkingConfigForm.quickTurnThreshold.options.hours4')}</MenuItem>
          </Select>
          {!thresholdValidation.isValid && (
            <FormHelperText>{thresholdValidation.message}</FormHelperText>
          )}
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title={t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.title')}
        helpText={t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.helpText')}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={sortingStrategy}
            onChange={(e) =>
              onSortingStrategyChange(e.target.value as SortingStrategy)
            }
          >
            <MenuItem
              value="arrival"
              disabled={disabledSortingOptions.includes("arrival")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.arrival')}
            </MenuItem>
            <MenuItem
              value="size-constrained"
              disabled={disabledSortingOptions.includes("size-constrained")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.sizeConstrained')}
            </MenuItem>
            <MenuItem
              value="longest-dwell"
              disabled={disabledSortingOptions.includes("longest-dwell")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.longestDwell')}
            </MenuItem>
            <MenuItem
              value="latest-departure"
              disabled={disabledSortingOptions.includes("latest-departure")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.latestDeparture')}
            </MenuItem>
            <MenuItem
              value="hybrid"
              disabled={disabledSortingOptions.includes("hybrid")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.hybrid')}
            </MenuItem>
            <MenuItem
              value="largest-first"
              disabled={disabledSortingOptions.includes("largest-first")}
            >
              {t('screens.ramp.autoPark.parkingConfigForm.sortingStrategy.options.largestFirst')}
            </MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>
    </Stack>
  );
};
